"use client";

import { useCallback } from "react";

import { getSuccessPageLocationMessage } from "@calcom/app-store/locations";
import dayjs from "@calcom/dayjs";
import { StripeInlinePayment } from "@calcom/features/bookings/components/payment/StripeInlinePayment";
import type { PollableStatus } from "@calcom/features/bookings/components/payment/useStripePaymentStatusPolling";
import { WEBAPP_URL } from "@calcom/lib/constants";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { BookingStatus } from "@calcom/prisma/enums";
import { trpc } from "@calcom/trpc/react";

import type { InlinePaymentState } from "../hooks/useBookings";

/**
 * PAY-E1 / US 1.1 — the inline payment step rendered inside the Booker (in place of the confirm form)
 * once a paid booking is created and the `inline-payment` flag is on. Shares the US 1.6 endpoint and
 * the `StripeInlinePayment` surface with the `/payment/[uid]` fallback so the two never diverge.
 */
export function BookerPaymentStep({
  inlinePayment,
  onPaid,
}: {
  inlinePayment: InlinePaymentState;
  onPaid: () => void;
}) {
  const { t, i18n } = useLocale();
  const utils = trpc.useUtils();
  const query = trpc.viewer.public.stripePaymentInfo.useQuery(
    { uid: inlinePayment.paymentUid },
    { refetchOnWindowFocus: false, retry: false }
  );

  // Poll through the vanilla client (no cache write): a `refetch()` would update `query.data`,
  // re-render this component and unmount the payment form mid-poll — dropping the resolution.
  const pollStatus = useCallback(async (): Promise<PollableStatus> => {
    const res = await utils.client.viewer.public.stripePaymentInfo.query({
      uid: inlinePayment.paymentUid,
    });
    return res.status;
  }, [utils, inlinePayment.paymentUid]);

  if (query.isPending) {
    return <p className="text-default p-6 text-center">{t("loading")}</p>;
  }

  if (query.isError || !query.data) {
    return <p className="text-error p-6 text-center">{t("payment_error_generic")}</p>;
  }

  const data = query.data;
  const isHold = data.paymentOption === "HOLD";

  if (data.status !== "payable") {
    const message =
      data.status === "paid"
        ? isHold
          ? t("payment_hold_card_saved")
          : t("paid")
        : data.status === "refunded"
          ? t("refunded")
          : t("payment_error_generic");
    const isTerminalOk = data.status === "paid" || data.status === "refunded";
    return <p className={`${isTerminalOk ? "text-default" : "text-error"} p-6 text-center`}>{message}</p>;
  }

  const date = dayjs(inlinePayment.startTime).locale(i18n.language ?? "en");
  // The booking is still awaiting payment at this point → PENDING (dynamic video links show
  // "details to follow" instead of a not-yet-generated meeting URL).
  const locationMessage = inlinePayment.location
    ? getSuccessPageLocationMessage(inlinePayment.location, t, BookingStatus.PENDING)
    : null;
  const recap = (
    <div className="text-default mb-4 grid grid-cols-3 gap-y-2 border-b pb-4 text-left text-sm">
      <div className="font-medium">{t("what")}</div>
      <div className="col-span-2">{inlinePayment.eventTitle}</div>
      <div className="font-medium">{t("when")}</div>
      <div className="col-span-2">{date.format("dddd, DD MMMM YYYY HH:mm")}</div>
      {locationMessage && (
        <>
          <div className="font-medium">{t("where")}</div>
          <div className="col-span-2">{locationMessage}</div>
        </>
      )}
    </div>
  );

  const bookingHref = data.bookingUid ? `/booking/${data.bookingUid}` : undefined;

  return (
    <div className="p-6">
      <h2 className="text-emphasis mb-4 text-lg font-semibold">{t("payment")}</h2>
      <StripeInlinePayment
        clientSecret={data.clientSecret}
        publishableKey={data.publishableKey}
        stripeAccount={data.stripeAccount}
        amount={data.amount}
        currency={data.currency}
        paymentOption={data.paymentOption}
        // Redirect-based methods (Bancontact/iDEAL/3DS fallback) land back on the payment page, which
        // re-reads the server-authoritative status (US 1.6) and cleans Stripe's query params.
        returnUrl={`${WEBAPP_URL}/payment/${inlinePayment.paymentUid}`}
        recap={recap}
        onPaid={onPaid}
        pollStatus={pollStatus}
        bookingHref={bookingHref}
      />
    </div>
  );
}

export default BookerPaymentStep;
